/**
 * TODO 7.3 — Step6AssignServices.jsx
 * 
 * Tela 6 — Serviços por Profissional (SÓ se equipe; 1 tela por profissional)
 * Esta tela é renderizada múltiplas vezes pelo container (uma por profissional a partir do 2º)
 * 
 * Props:
 *   wizard = useWelcomeWizard()
 *   professionalIndex = índice do profissional (0-based)
 * 
 * Estado guardado em wizard.state.professionals[i].serviceIds = [0, 1, ...]
 * 
 * Comportamento:
 *   - Todos os checkboxes começam marcados por padrão
 *   - Profissional 0: todos marcados
 *   - Profissional 2+: pré-marcados com a seleção do profissional anterior (i-1)
 *   - Desabilita "Continuar" se nenhum serviço estiver marcado
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Stack,
  Alert,
} from '@mui/material';
import { getWelcomeText } from '../../../utils/welcomeText';
import { wlog } from '../../../utils/welcomeDebug';

export default function Step6AssignServices({ wizard, professionalIndex, goNext }) {
  wlog('Step6AssignServices', 'RENDER', {
    professionalIndex,
    templateId: wizard.template?.id || 'NENHUM',
  });
  const { template, state } = wizard;
  const professional = state.professionals?.[professionalIndex];
  const services = state.services || [];

  const [selectedServiceIds, setSelectedServiceIds] = useState(new Set());

  // Inicializa seleção: todos por padrão, ou herda do profissional anterior
  useEffect(() => {
    let initialSelection = new Set();

    if (professionalIndex === 0) {
      // Profissional 0: todos marcados
      initialSelection = new Set(services.map((s) => s.id));
    } else {
      // Profissional 2+: herda do anterior
      const prevProfessional = state.professionals?.[professionalIndex - 1];
      if (prevProfessional?.serviceIds) {
        initialSelection = new Set(prevProfessional.serviceIds);
      } else {
        // Fallback: todos marcados
        initialSelection = new Set(services.map((s) => s.id));
      }
    }

    // Se já tem dados salvos para este profissional, usa eles
    if (professional?.serviceIds) {
      initialSelection = new Set(professional.serviceIds);
    }

    setSelectedServiceIds(initialSelection);
  }, [professional, services, professionalIndex, state.professionals]);

  // Textos temáticos
  const [titleText, setTitleText] = useState('');
  const [hintText, setHintText] = useState('');

  useEffect(() => {
    const loadText = async () => {
      const firstName = state.professionals?.[0]?.name ?? '';
      const profName = professional?.name ?? `Profissional ${professionalIndex}`;

      const title = await getWelcomeText(template, 'assignTitle', {
        professionalName: profName,
        firstName,
      });
      const hint = await getWelcomeText(template, 'assignHint', {
        professionalName: profName,
      });

      setTitleText(title);
      setHintText(hint);
    };
    loadText().catch(console.error);
  }, [template, professional, state.professionals, professionalIndex]);

  // Toggle serviço
  const handleToggleService = (serviceId) => {
    setSelectedServiceIds((prev) => {
      const updated = new Set(prev);
      if (updated.has(serviceId)) {
        updated.delete(serviceId);
      } else {
        updated.add(serviceId);
      }
      return updated;
    });
  };

  // Continuar
  const handleContinue = () => {
    if (selectedServiceIds.size === 0) return;

    const serviceIdArray = Array.from(selectedServiceIds);
    wizard.setProfessionalServiceIds(professionalIndex, serviceIdArray);
    wizard.goNext();
  };

  const hasNoneSelected = selectedServiceIds.size === 0;

  const colors = wizard.template?.colors || {};

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        backgroundColor: colors.backgroundPaper || colors.background || '#f9f9f9',
      }}
    >
      <Container maxWidth="sm">
        <section>
          {/* Título temático */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              marginBottom: '8px',
              textAlign: 'center',
              color: colors.text || '#000',
            }}
          >
            {titleText}
          </Typography>

          {/* Hint temático */}
          <Typography
            variant="body2"
            sx={{
              color: colors.textSecondary || 'rgba(0,0,0,0.6)',
              marginBottom: '24px',
              textAlign: 'center',
              lineHeight: 1.5,
            }}
          >
            {hintText}
          </Typography>

          {/* Aviso se nenhum selecionado */}
          {hasNoneSelected && (
            <Alert severity="warning" sx={{ marginBottom: '16px' }}>
              Selecione pelo menos um serviço para continuar.
            </Alert>
          )}

          {/* Lista de checkboxes */}
          <Stack spacing={1} sx={{ marginBottom: '32px' }}>
            {services.map((service) => {
              const isSelected = selectedServiceIds.has(service.id);
              return (
                <FormControlLabel
                  key={service.id}
                  control={
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleToggleService(service.id)}
                    />
                  }
                  label={service.name}
                  sx={{
                    padding: '8px',
                    margin: '0',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      backgroundColor: colors.backgroundPaper || 'rgba(0,0,0,0.04)',
                    },
                    '& .MuiFormControlLabel-label': {
                      fontWeight: isSelected ? 500 : 400,
                      color: colors.text || '#000',
                    },
                  }}
                />
              );
            })}
          </Stack>

          {/* Botão Continuar */}
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleContinue}
              disabled={hasNoneSelected}
              sx={{
                textTransform: 'none',
                minWidth: '120px',
                padding: '10px 24px',
              }}
            >
              Continuar
            </Button>
          </Box>
        </section>
      </Container>
    </Box>
  );
}
